import type { SupportedLanguage } from 'shared/i18n/i18n';

export const STORE_TYPES = [
  'Coffe Store',
  'Roastery',
  'Plastic',
  'Accessories',
  'Home Accessories',
  'Beauty',
  'Restaurant',
  'Sweets',
  'Konditorei',
  'Bakery',
  'Fruit & Vegetables',
  'Flowers',
  '(None)',
  'Supermarket',
  'Pharmacy',
  'Meat Butchery',
  'Electronics Store',
  'Fashion',
  'Grocery',
  'Fishery',
  'Pet Store',
  'Gifts Shop',
  'Books Shop',
  'Eyewear Store',
  'Toys Store',
] as const;

export type StoreType = (typeof STORE_TYPES)[number];

/**
 * Slug used as i18n key for a store type label.
 * `'Fruit & Vegetables' -> 'fruitVegetables'`, `'(None)' -> 'none'`.
 */
export const storeTypeKey = (s: string): string => {
  const trimmed = s.replace(/[()]/g, '').trim();
  const parts = trimmed.split(/[^A-Za-z0-9]+/).filter(Boolean);
  return parts
    .map((p, i) =>
      i === 0
        ? p.charAt(0).toLowerCase() + p.slice(1)
        : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    )
    .join('');
};

export type OnboardingStatus =
  | 'Draft'
  | 'Submitted'
  | 'Contract Pending'
  | 'Contract Signed'
  | 'Menu Creation'
  | 'Device Installation'
  | 'Ready To Launch'
  | 'Live';

export type DayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export const DAYS: DayKey[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export interface DayInterval {
  /** 'HH:MM' (24h). */
  open: string;
  /** 'HH:MM' (24h). */
  close: string;
}

export type DayMode = 'specific' | '24h';

export interface DayHours {
  /** Whether the business is open this day at all (toggle switch in the picker). */
  enabled: boolean;
  /** When `specific`, `intervals` define open ranges. When `24h`, the day is open all day. */
  mode: DayMode;
  /** One or more open ranges (split shifts). Ignored when `mode === '24h'` or `enabled === false`. */
  intervals: DayInterval[];
}

export type OpeningHours = Record<DayKey, DayHours>;

/**
 * Business category the store belongs to. Drives which store-type list is
 * shown on the first step (Restaurant types vs Market types) and is derived
 * from the hub store-type `category` field.
 */
export const BUSINESS_CATEGORIES = ['restaurant', 'market'] as const;
export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number];

export interface BusinessInfo {
  commercialName: string;
  ownerName: string;
  phoneNumber: string;
  businessPhoneNumber: string;
  email: string;
  /** Restaurant vs Market. Empty until the partner picks a category. */
  businessCategory: BusinessCategory | '';
  storeType: StoreType | '';
  location: string;
  address: string;
  /** Days the business operates. Feeds the opening-hours picker (unselected days are closed). */
  workingDays: DayKey[];
  latitude?: number | null;
  longitude?: number | null;
}

export interface FinalDetails {
  openingHours: OpeningHours;
  notes: string;
}

/**
 * Known delivery competitors the partner can select. `'other'` is a sentinel
 * that reveals the free-text input where the user types unlisted companies.
 * Values are stable keys (not display labels) so we can localise without
 * losing the stored selection.
 */
export const DELIVERY_COMPANIES = ['wolt', '10bis', 'cibus', 'mishloha', 'other'] as const;
export type DeliveryCompany = (typeof DELIVERY_COMPANIES)[number];

/**
 * Kosher certification types a kosher business can hold. Stable keys (not
 * display labels) so the selection survives localisation.
 */
export const KOSHER_CERTIFICATIONS = ['regular', 'badatz', 'mehadrin', 'zohar'] as const;
export type KosherCertification = (typeof KOSHER_CERTIFICATIONS)[number];

/** Dietary / specialty sensitivities on the category step. */
export const SENSITIVITIES = [
  'vegan',
  'gluten_free',
  'sugar_free',
  'parve',
] as const;
export type Sensitivity = (typeof SENSITIVITIES)[number];

/**
 * How the partner will provide their menu. Drives the conditional UI on the
 * questions step:
 *   - `upload`  : upload a file (image / PDF / Excel / Word)
 *   - `website` : provide a public link to a page that contains the menu
 *   - `pos`     : integrate with their POS, with an optional menu file
 */
export const MENU_SOURCES = ['upload', 'website', 'pos'] as const;
export type MenuSource = (typeof MENU_SOURCES)[number];

export interface BusinessQuestions {
  /**
   * Not asked as a yes/no: derived from `deliveryCompanies` being non-empty.
   * Pipedrive and the statistics dashboard still consume the boolean.
   */
  worksWithOtherDeliveryCompanies?: boolean | null;
  /** Selected known competitors. When it contains `'other'`, `otherDeliveryCompanies` holds the typed list. */
  deliveryCompanies: DeliveryCompany[];
  /** Free-text companies typed by the user when `'other'` is selected. */
  otherDeliveryCompanies: string;
  /** Not asked as a yes/no: derived from `kosherCertifications` being non-empty. */
  isKosher: boolean | null;
  /** Selected kosher certification types. Empty means the business is not kosher. */
  kosherCertifications: KosherCertification[];
  /** Dietary specialties (vegan, gluten-free, …). */
  sensitivities: Sensitivity[];
  /** Free-text store type when the partner picks "Other". */
  storeTypeOther?: string;
  /** How the partner provides their menu. `null` until chosen. */
  menuSource: MenuSource | null;
  /** Public URL to a page containing the menu. Only when `menuSource === 'website'`. */
  menuWebsiteUrl?: string;
  /** Uploaded menu. Required for `upload` and optional for `pos`. */
  menuFile?: FileMeta;
  /** Selected POS system name, or free text for "other". Only when `menuSource === 'pos'`. */
  posType?: string;
  /** Whether the partner wants HAAT to run a menu photo shoot for them. */
  wantsMenuPhotoShoot: boolean | null;
}

export interface TaxLegalInfo {
  taxId: string;
  officialBusinessName: string;
}

export interface BankAccountInfo {
  bankName: string;
  bankBranchNumber: string;
  bankAccountNumber: string;
  bankAccountOwnerName: string;
  bankAccountOwnerId: string;
}

export interface RepresentativeInfo {
  representativeName: string;
  representativePhone: string;
  representativePosition: string;
  /** @deprecated Kept optional for legacy drafts; no longer collected. */
  representativeId?: string;
  /** @deprecated Kept optional for legacy drafts; no longer collected. */
  representativeEmail?: string;
}

/**
 * Files are uploaded to the InsertProductsMarket backend and we persist
 * only the returned CDN URL + metadata. This keeps the localStorage draft
 * tiny (no base64 blobs) so auto-save never blows the quota.
 */
export interface FileMeta {
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedAt: string;
}

export type DocumentKey =
  | 'osekCertificate'
  | 'taxClearance'
  | 'bankConfirmation'
  | 'businessLogo';

export interface Documents {
  osekCertificate?: FileMeta;
  taxClearance?: FileMeta;
  bankConfirmation?: FileMeta;
  businessLogo?: FileMeta;
}

export interface OnboardingMetadata {
  language: SupportedLanguage;
  currentStep: number;
  /**
   * Highest step the user has ever advanced to. Used to gate the
   * stepper navigation: users may jump back to any visited step but
   * cannot skip forward past unfilled sections.
   */
  maxStepReached: number;
  submissionId?: string;
  createdAt: string;
  status: OnboardingStatus;
  /** Whether the contact-step mobile number answered its SMS challenge. */
  phoneVerified: boolean;
  /**
   * The number that was actually verified. Editing the phone afterwards
   * invalidates the verification, so the two are compared rather than
   * trusting the flag alone.
   */
  verifiedPhone?: string;
  /** Short-lived proof of verification; replayed on final submit. */
  phoneVerificationToken?: string;
  /**
   * Active SMS challenge, kept so the business step can resume it after
   * navigating away instead of burning another message. It implicitly belongs
   * to `businessInfo.phoneNumber`, which clears it whenever the number changes.
   */
  otpChallengeId?: string;
  otpSentTo?: string;
  /**
   * Wizard layout version. Bumped when step indices change so old drafts
   * reset navigation while preserving entered data.
   */
  flowVersion?: number;
}

export interface OnboardingData {
  businessInfo: BusinessInfo;
  businessQuestions: BusinessQuestions;
  taxLegalInfo: TaxLegalInfo;
  bankAccountInfo: BankAccountInfo;
  representativeInfo: RepresentativeInfo;
  finalDetails: FinalDetails;
  documents: Documents;
  metadata: OnboardingMetadata;
}

export type OnboardingSectionKey =
  | 'businessInfo'
  | 'businessQuestions'
  | 'taxLegalInfo'
  | 'bankAccountInfo'
  | 'representativeInfo'
  | 'finalDetails';
